/**
 * The access-revision store and the fence the fetch wrappers raise around it
 * (E1.5 PR 4b, #1336; spec `docs/plans/2026-10-01-dashboard-e15-access-grants.md`,
 * «UI — PR 4b»).
 *
 * The server stamps `X-Access-Revision` on every answer that read the
 * caller's access policy. The revision is ORGANIZATION-WIDE: it grows on any
 * role, project, structure, external-id or grant change of anyone in the
 * organization. So a growth says «someone's access may have changed», not
 * «yours did» — whether the viewer's own access changed is decided by
 * comparing `/access/me` answers (`useAccessMe`), not here.
 *
 * The UI keeps ONE number per (organization, subject) — `known`, the highest
 * header seen — and uses it three ways:
 *
 * - it is sent back on every request (`Vary` partitions the browser cache by
 *   it; the server never reads it as authority);
 * - a GET answer stamped LOWER than `known` is a late answer of an access that
 *   may no longer hold: the wrapper throws `StaleAccessRevisionError` and the
 *   body reaches nobody. The error is retryable — asked again, the request
 *   leaves with `known` and comes back current. A write is never refused: the
 *   server already committed it;
 * - a HIGHER one moves `known` up and tells the growth handler (`useAccessMe`
 *   re-reads `/access/me`) — unless the answer that taught it IS `/access/me`.
 *
 * The first number a pair learns is not a growth: there is nothing older to
 * correct. An answer with no header does not depend on the revision (an owner
 * whose revision is unknown, another organization's metrics, a route that
 * never read the policy) and passes through untouched. For the same reason
 * the BODY of `/access/me` teaches nothing: an owner's `revision: 0` without a
 * header is «unknown», not «revision 0».
 *
 * The store also counts the viewer's access CHANGES (`noteAccessChange`,
 * called by `useAccessMe` when its answer differs) — what open panels close
 * on (`hooks/useAccessChange.ts`).
 *
 * This module imports nothing from the fetch layer, so the embedded hook can
 * use it while tests stub `fetchJSON` (see `request-headers.ts`).
 */
import { ACCESS_REVISION_HEADER } from '@aica/api-types/schemas';

/**
 * A late GET answer of an older revision than the UI already knows. Not a
 * failure of the request: asked again, it leaves with the revision now known
 * (`retryTransientOnly`, `retryStaleAccessOnce`).
 */
export class StaleAccessRevisionError extends Error {
  constructor(
    public readonly seen: number,
    public readonly known: number
  ) {
    super(`Answer of access revision ${seen} arrived after revision ${known} was known`);
    this.name = 'StaleAccessRevisionError';
  }
}

/**
 * The identity roster (`user-data`) is the one cache independent of the
 * organization, the subject and their access: it outlives every sweep — the
 * organization switch (`OrganizationContext`), the pair switch and an access
 * change (`useAccessMe`).
 */
export function isIdentityQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === 'user-data';
}

interface StoreState {
  orgId: string | null;
  subjectId: string | null;
  /** `null` — no revision has reached the UI for this pair yet. */
  known: number | null;
  /** Bumped whenever the pair changes: tells a request which store it left from. */
  generation: number;
  /** How many times the viewer's access changed — never reset, only grows. */
  accessChanges: number;
}

const EMPTY_STATE: StoreState = {
  orgId: null,
  subjectId: null,
  known: null,
  generation: 0,
  accessChanges: 0,
};

let state: StoreState = EMPTY_STATE;
const listeners = new Set<() => void>();
type GrowthHandler = (orgId: string | null, subjectId: string | null) => void;
let growthHandler: GrowthHandler | null = null;

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeAccessRevision(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The revision known for exactly this pair, `null` for any other: a pair the
 * store is not bound to has nothing known (A16 — a previous subject's number
 * is never read for the next).
 */
export function readKnownRevision(
  orgId: string | null,
  subjectId: string | null
): number | null {
  return state.orgId === orgId && state.subjectId === subjectId ? state.known : null;
}

export interface AccessPair {
  orgId: string | null;
  subjectId: string | null;
}

/**
 * Bind the store to the pair the session is now looking at. A different pair
 * resets it (A16). Returns the pair it left when it changed, `null` when it
 * was already bound to this one.
 */
export function bindAccessPair(
  orgId: string | null,
  subjectId: string | null
): AccessPair | null {
  if (state.orgId === orgId && state.subjectId === subjectId) return null;
  const previous: AccessPair = { orgId: state.orgId, subjectId: state.subjectId };
  state = {
    orgId,
    subjectId,
    known: null,
    generation: state.generation + 1,
    accessChanges: state.accessChanges,
  };
  emit();
  return previous;
}

/** Who gets told when a response teaches the UI a newer revision. */
export function setAccessRevisionGrowthHandler(handler: GrowthHandler): () => void {
  growthHandler = handler;
  return () => {
    if (growthHandler === handler) growthHandler = null;
  };
}

/** The viewer's access changed (`useAccessMe`): open panels close on it. */
export function noteAccessChange(): void {
  state = { ...state, accessChanges: state.accessChanges + 1 };
  emit();
}

/** How many times the viewer's access has changed in this session. */
export function readAccessChanges(): number {
  return state.accessChanges;
}

/**
 * What a request is, as far as the fence cares:
 * - `read` — a GET, or a non-GET the caller declared read-only (`accessRead`,
 *   e.g. the semantic search POST): its late answer is refused;
 * - `write` — anything else: the server committed it, its answer only teaches;
 * - `access-me` — the source of the access itself: never refused, and a
 *   growth it teaches does not ask it again.
 */
export type AccessFenceKind = 'read' | 'write' | 'access-me';

/** What a request left with — enough to judge its answer. */
export interface AccessFence {
  generation: number;
  kind: AccessFenceKind;
  /** The revision sent with the request, `null` when none was known. */
  sent: number | null;
}

/** `/api/v1/access/me` and its embedded mirror, with or without a query. */
const ACCESS_ME_PATH = /\/access\/me(?:[?#]|$)/;

/**
 * Open a fence for a request to `orgId`. `null` when the store is not bound to
 * that organization (another organization's request, or a session that has
 * not bound yet): nothing to send and nothing to check.
 *
 * Open it AFTER every await that precedes the request (auth headers, a CSRF
 * refresh): a fence opened earlier would carry a revision — or a pair — the
 * request no longer leaves under.
 */
export function beginAccessFence(
  orgId: string | null | undefined,
  request: { method?: string; url: string; read?: boolean }
): AccessFence | null {
  if (!orgId || state.orgId !== orgId) return null;
  const method = (request.method ?? 'GET').toUpperCase();
  const kind: AccessFenceKind = ACCESS_ME_PATH.test(request.url)
    ? 'access-me'
    : method === 'GET' || request.read === true
      ? 'read'
      : 'write';
  return { generation: state.generation, kind, sent: state.known };
}

/** The request header that carries the known revision — empty while none is. */
export function accessRevisionRequestHeaders(
  fence: AccessFence | null
): Record<string, string> {
  return fence && fence.sent !== null
    ? { [ACCESS_REVISION_HEADER]: String(fence.sent) }
    : {};
}

function parseRevision(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

/**
 * Judge an answer by its header. A GET answer of an older revision than the
 * one now known throws `StaleAccessRevisionError`; any answer of a newer one
 * raises `known` (and, unless it is `/access/me`'s own, tells the growth
 * handler); an answer without the header passes.
 *
 * An answer to a request that left under another pair (the session switched
 * organization or subject while it was in flight) is that pair's business:
 * the queries it fed were cancelled and removed with the switch, and its
 * number must neither raise nor be compared with the new pair's.
 */
export function judgeAccessResponse(
  fence: AccessFence | null,
  headers: Headers | undefined
): void {
  if (!fence || !headers) return;
  const seen = parseRevision(headers.get(ACCESS_REVISION_HEADER));
  if (seen === null) return;
  if (state.generation !== fence.generation) return;
  const previous = state.known;
  if (previous !== null && seen <= previous) {
    if (seen < previous && fence.kind === 'read') {
      throw new StaleAccessRevisionError(seen, previous);
    }
    return;
  }
  state = { ...state, known: seen };
  emit();
  if (previous !== null && fence.kind !== 'access-me') {
    growthHandler?.(state.orgId, state.subjectId);
  }
}

/**
 * A GET whose answer is a raw `Response` (binary: record audio) rather than
 * JSON, so it cannot go through `fetchJSON`: the same fence around a bare
 * `fetch`. The known revision rides along, and the answer is judged BEFORE
 * the caller reads its body or status — a late answer of an older revision
 * throws `StaleAccessRevisionError` and the body reaches nobody.
 *
 * `orgId` is the organization the request is for; the fence opens here, after
 * the caller's last await. Every raw-`Response` GET of access-dependent data
 * goes through this. A stale answer is asked again ONCE; a second stale one
 * throws to the caller.
 */
export async function fetchRawWithAccessFence(
  orgId: string,
  url: string,
  init: Omit<RequestInit, 'headers' | 'method'> & { headers?: Record<string, string> }
): Promise<Response> {
  const once = async (): Promise<Response> => {
    const fence = beginAccessFence(orgId, { method: 'GET', url });
    const response = await fetch(url, {
      ...init,
      headers: { ...init.headers, ...accessRevisionRequestHeaders(fence) },
    });
    judgeAccessResponse(fence, response.headers);
    return response;
  };
  try {
    return await once();
  } catch (error) {
    // A safe GET overtaken by a newer revision is asked once more, under the
    // revision now known — as React Query and the search do; otherwise the
    // player stays unavailable until its panel remounts (Codex #1745).
    if (!(error instanceof StaleAccessRevisionError)) throw error;
    return once();
  }
}

/** Test seam: the module is a singleton, a test starts from an empty store. */
export function resetAccessRevisionStoreForTests(): void {
  state = EMPTY_STATE;
  listeners.clear();
  growthHandler = null;
}
